import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";

const TABS = {
  LOGIN: "login",
  REGISTER: "register",
  FORGOT: "forgot",
  RESET: "reset",
};

export default function AuthScreen() {
  const { login, register, forgotPassword, resetPassword, loading, sessionExpired } = useAuth();
  const [tab, setTab] = useState(TABS.LOGIN);
  const [identifier, setIdentifier] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  const formatError = (e) => {
    const detail = e?.response?.data?.detail;
    if (Array.isArray(detail)) {
      // FastAPI 422: array of {loc, msg, type}
      return detail
        .map((d) => {
          const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : "field";
          return `${field}: ${d.msg}`;
        })
        .join(" · ");
    }
    if (typeof detail === "string") return detail;
    if (detail) return JSON.stringify(detail);
    return e?.message || "Something went wrong";
  };

  const wrap = async (fn) => {
    setError(null);
    setInfo(null);
    try {
      await fn();
    } catch (e) {
      setError(formatError(e));
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    wrap(() => login(identifier, password));
  };

  const handleRegister = (e) => {
    e.preventDefault();
    wrap(() => register(email, username, password));
  };

  const handleForgot = (e) => {
    e.preventDefault();
    wrap(async () => {
      const res = await forgotPassword(email);
      setInfo(
        res.reset_token
          ? `Reset token (dev mode): ${res.reset_token}`
          : "If that account exists, a reset email has been sent.",
      );
      if (res.reset_token) {
        setResetToken(res.reset_token);
        setTab(TABS.RESET);
      }
    });
  };

  const handleReset = (e) => {
    e.preventDefault();
    wrap(async () => {
      await resetPassword(resetToken, newPassword);
      setInfo("Password updated. You can now log in.");
      setTab(TABS.LOGIN);
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 p-6">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200/80 dark:border-slate-800 p-7">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-sm shadow-indigo-500/20">
            <svg width="18" height="18" viewBox="0 0 24 24" className="text-white" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 17h2l2-6 2 4 2-7 2 9 2-5 2 5h2"/></svg>
          </div>
          <div>
            <h1 className="text-base font-semibold">Vizaura</h1>
            <p className="text-xs text-slate-400 dark:text-slate-500">Sign in to continue</p>
          </div>
        </div>

        <div className="flex gap-1 mb-5 text-xs">
          {[
            [TABS.LOGIN, "Login"],
            [TABS.REGISTER, "Register"],
            [TABS.FORGOT, "Forgot"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => { setTab(key); setError(null); setInfo(null); }}
              className={`px-3 py-1.5 rounded-lg border transition ${
                tab === key
                  ? "bg-indigo-600 border-indigo-600 text-white"
                  : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-indigo-300"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {sessionExpired && !error && (
          <div className="mb-4 text-xs px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300">
            Your session expired. Please sign in again to continue.
          </div>
        )}
        {error && (
          <div className="mb-4 text-xs px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}
        {info && (
          <div className="mb-4 text-xs px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 break-all">
            {info}
          </div>
        )}

        {tab === TABS.LOGIN && (
          <form onSubmit={handleLogin} className="space-y-3">
            <Field label="Email or username" value={identifier} onChange={setIdentifier} type="text" />
            <Field label="Password" value={password} onChange={setPassword} type="password" />
            <SubmitButton loading={loading}>Log in</SubmitButton>
          </form>
        )}

        {tab === TABS.REGISTER && (
          <form onSubmit={handleRegister} className="space-y-3">
            <Field label="Email" value={email} onChange={setEmail} type="email" />
            <Field label="Username" value={username} onChange={setUsername} type="text" />
            <Field label="Password" value={password} onChange={setPassword} type="password" />
            <SubmitButton loading={loading}>Create account</SubmitButton>
          </form>
        )}

        {tab === TABS.FORGOT && (
          <form onSubmit={handleForgot} className="space-y-3">
            <Field label="Email" value={email} onChange={setEmail} type="email" />
            <SubmitButton loading={loading}>Send reset link</SubmitButton>
          </form>
        )}

        {tab === TABS.RESET && (
          <form onSubmit={handleReset} className="space-y-3">
            <Field label="Reset token" value={resetToken} onChange={setResetToken} type="text" />
            <Field label="New password" value={newPassword} onChange={setNewPassword} type="password" />
            <SubmitButton loading={loading}>Set new password</SubmitButton>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
        autoComplete="off"
        required
      />
    </label>
  );
}

function SubmitButton({ loading, children }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white text-sm font-medium py-2 rounded-lg transition"
    >
      {loading ? "..." : children}
    </button>
  );
}
